/**
 * HeroSection Component
 *
 * Diamond Architecture: the static half of the hero. Both search controls are
 * interactive islands beside it — the desktop one arrives through `searchSlot`
 * (`DockableHeroSearch`, which has to be a portal source) and the mobile one is
 * {@link HeroSearchForm}.
 *
 * Features:
 * - Art-directed backgrounds via `<picture>` + `<source media>`
 *   (only the matching image downloads — no double-fetch)
 * - Media-scoped preload links for LCP optimization
 * - Logo tile badge at bottom center
 *
 * Matches Rails: Home::HeroComponent
 */
import { useTranslations } from '@/shared/i18n';
import { cn } from '@letsgethome/ui/lib/utils';
import { Z_LAYERS } from '@letsgethome/ui/z-layers';

import { HeroSearchForm } from './HeroSearchForm';

type HeroSectionProps = {
  /**
   * The desktop search island. REQUIRED, and that is the fix: it was optional
   * with a `?? <HeroSearch />` fallback, and `home.tsx` — the only caller —
   * has always passed it, so `HeroSearch.tsx` was a whole module nothing could
   * render. `knip` could not see that: the file WAS imported and its export WAS
   * used, in a branch whose condition is always false. A dead fallback is not
   * an unused export, it is an untyped default — so the remedy is the type,
   * and the module is deleted.
   */
  searchSlot: React.ReactNode;
};

export function HeroSection({ searchSlot }: HeroSectionProps) {
  const t = useTranslations('home.hero');

  return (
    // NO z-index on this section, so nothing here creates a stacking context
    // its children would be trapped inside. (This comment used to name a
    // level no element in the tree carries — and because Tailwind's scanner
    // reads comments, that sentence MINTED a dead rule into every app's
    // stylesheet. See `z-layers`.)
    <section className="relative px-4 pt-4 sm:px-6 lg:px-8">
      <div className="relative mx-auto max-w-7xl">
        {/* Content container - NO transforms here */}
        <div className="relative py-10 md:py-24 px-4">
          {/* Background — art-directed via <picture>, only one image downloads per device */}
          <div
            className={cn(
              'absolute inset-0 rounded-3xl overflow-hidden shadow-sm',
              Z_LAYERS.decorativeBackdrop.className
            )}
          >
            {/* Media-scoped preloads — each device preloads only its image */}
            <link
              rel="preload"
              as="image"
              href="/home/hero_background_desktop.webp"
              media="(min-width: 768px)"
              fetchPriority="high"
            />
            <link
              rel="preload"
              as="image"
              href="/home/hero_background_mobile.webp"
              media="(max-width: 767px)"
              fetchPriority="high"
            />
            <picture>
              <source media="(min-width: 768px)" srcSet="/home/hero_background_desktop.webp" />
              <img
                src="/home/hero_background_mobile.webp"
                alt=""
                aria-hidden="true"
                decoding="async"
                loading="eager"
                fetchPriority="high"
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </picture>
          </div>

          {/* Content */}
          <h1 className="tracking-[-0.5px] mb-4 md:mb-10 text-2xl md:text-6xl text-center font-bold">
            {t('title')}
          </h1>

          {/* Search - Desktop (slot for DockableHeroSearch) */}
          <div className="hidden md:block max-w-xl mx-auto">{searchSlot}</div>

          {/* Search - Mobile: a real native GET form onto the browse hub,
              enhanced into the shared full-screen `SearchOverlay` — the same
              surface the browse canvas opens. It used to be the desktop
              combobox rendered a second time, which on a 393 px phone meant a
              suggestions panel dropped under the hero and no dialog at all;
              then it was an ANCHOR to the hub, which is a no-JS path only if
              the hub has a search field of its own, and the hub's was this
              same anchor. */}
          <div className="md:hidden max-w-xs mx-auto">
            <HeroSearchForm />
          </div>
        </div>

        {/* Logo tile - OUTSIDE content container to avoid transform trap */}
        <div
          className={cn(
            'absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 pointer-events-none',
            Z_LAYERS.decorativeBackdrop.className
          )}
        >
          <img
            src="/brand/logo-tile.webp"
            alt=""
            aria-hidden="true"
            width={40}
            height={40}
            className="size-10 md:size-16 rounded-full"
            loading="eager"
            decoding="async"
          />
        </div>
      </div>
      {/* Sentinel for scroll detection (matches Rails) */}
      <div id="hero-sentinel" aria-hidden="true" />
    </section>
  );
}
